import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, MenuItem, Paper, Skeleton, TextField, Tooltip, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPaperclip } from 'react-icons/fa';
import { IoSendSharp } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { getData, postData, postFileUpload } from '../../Webservice/Webservice';
import { FONT, PriorityChip, StatusChip, authToken, dt, initial, yellowBtn } from './ticketShared';

const MAX_FILES = 5;
const MAX_MB = 5;

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const Bubble = ({ mine, customer, name, text, images, time }) => (
  <Box sx={{ display: 'flex', justifyContent: customer ? 'flex-start' : 'flex-end', mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, maxWidth: { xs: '92%', md: '72%' }, flexDirection: customer ? 'row' : 'row-reverse' }}>
      <Tooltip title={name}><Avatar sx={{ width: 30, height: 30, fontSize: 12.5, fontWeight: 800, bgcolor: customer ? 'rgb(255, 209, 2)' : mine ? '#4338ca' : '#334155', color: customer ? 'black' : 'white' }}>{initial(name)}</Avatar></Tooltip>
      <Paper elevation={0} sx={{ p: 1.75, borderRadius: customer ? '16px 16px 16px 4px' : '16px 16px 4px 16px', bgcolor: customer ? '#fff' : mine ? '#4338ca' : '#334155', color: customer ? '#0f172a' : '#fff', border: customer ? '1px solid #e2e8f0' : 'none' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 800, opacity: 0.75, mb: 0.25 }}>{name}</Typography>
        {text && <Typography sx={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Typography>}
        {images?.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {images.map((img, i) => <img key={i} src={img} alt="" onClick={() => window.open(img, '_blank')} style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(0,0,0,.08)' }} />)}
          </Box>
        )}
        <Typography sx={{ fontFamily: FONT, fontSize: 11, opacity: 0.65, mt: 0.75, textAlign: customer ? 'left' : 'right' }}>{dt(time)}</Typography>
      </Paper>
    </Box>
  </Box>
);

const Chat = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const endRef = useRef(null);
  const [query, setQuery] = useState(null);
  const [messages, setMessages] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [confirm, setConfirm] = useState(null); // 'resolved' | 'open'
  const [working, setWorking] = useState(false);

  const load = useCallback(async (quiet = false) => {
    try {
      const res = await getData(`query/view/${queryId}`, null, authToken(), false);
      if (res?.success) {
        setQuery(res.query);
        setMessages(res.messages || []);
        setError('');
      } else if (!quiet) setError('Could not load this ticket.');
    } catch {
      if (!quiet) setError('Could not load this ticket.');
    } finally {
      setLoading(false);
    }
  }, [queryId]);

  useEffect(() => { load(); }, [load]);
  // pick up new customer messages without a refresh
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) load(true); }, 15000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);
  useEffect(() => {
    getData('query/list', { limit: 1 }, authToken(), false).then((r) => setStaff(r?.staff || [])).catch(() => {});
  }, []);

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const next = [...files];
    for (const f of picked) {
      if (!f.type.startsWith('image/')) { setSendError('Only images can be attached.'); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { setSendError(`Each image must be under ${MAX_MB} MB.`); continue; }
      if (next.length >= MAX_FILES) { setSendError(`You can attach up to ${MAX_FILES} images.`); break; }
      next.push(f);
    }
    setFiles(next);
  };

  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    setSending(true);
    setSendError('');
    try {
      const body = new FormData();
      body.append('queryId', queryId);
      body.append('message', text.trim());
      files.forEach((f) => body.append('images', f));
      await postFileUpload('query/reply', body, authToken());
      setText('');
      setFiles([]);
      await load(true);
    } catch (err) {
      setSendError(err?.message || 'Could not send the reply.');
    } finally {
      setSending(false);
    }
  };

  const change = async (body) => {
    setWorking(true);
    try {
      await postData(`query/update/${queryId}`, body, authToken(), false);
      await load(true);
    } catch { /* the service shows the error */ } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={90} sx={{ mb: 2, borderRadius: '14px' }} /><Skeleton variant="rounded" height={380} sx={{ borderRadius: '14px' }} /></Box>;
  }
  if (error && !query) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button onClick={() => navigate('/queries')} sx={yellowBtn}>Back to support tickets</Button>
      </Box>
    );
  }

  const resolved = query.status === 'resolved';
  const customerName = query.customerId?.name || 'Customer';

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 2, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <IconButton onClick={() => navigate('/queries')} sx={{ background: '#f1f5f9' }}><FaArrowLeft size={15} /></IconButton>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, color: '#0f172a' }}>{query.subject || 'Support ticket'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{query.referenceNo} · {customerName} · opened {dt(query.createdAt)}</Typography>
        </Box>
        <StatusChip status={query.status} />
        <PriorityChip priority={query.priority} />
        {resolved ? (
          <Button disabled={working} onClick={() => setConfirm('open')} sx={{ textTransform: 'none', fontWeight: 800, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>Reopen ticket</Button>
        ) : (
          <>
            {query.status === 'open' && <Button disabled={working} onClick={() => change({ status: 'in-progress' })} sx={{ textTransform: 'none', fontWeight: 800, border: '1px solid #fcd34d', borderRadius: '10px', color: '#92400e' }}>Mark in progress</Button>}
            <Button disabled={working} onClick={() => setConfirm('resolved')} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#16a34a', '&:hover': { background: '#15803d' } }}>Resolve ticket</Button>
          </>
        )}
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' }, alignItems: 'start' }}>
        {/* conversation */}
        <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 480 }}>
          <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: '58vh', p: 2.5, background: '#f1f5f9' }}>
            <Bubble customer name={customerName} text={query.description} images={query.images} time={query.createdAt} />
            {messages.map((m) => (
              <Bubble key={m._id} customer={m.senderRole === 'Customer'} mine={m.mine} name={m.senderName} text={m.message} images={m.images} time={m.createdAt} />
            ))}
            {messages.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', textAlign: 'center', mt: 2 }}>No replies yet. Be the first to respond.</Typography>}
            <div ref={endRef} />
          </Box>

          {!resolved ? (
            <Box sx={{ p: 1.75, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
              {files.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
                  {files.map((f, i) => (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <img src={URL.createObjectURL(f)} alt={f.name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                      <IconButton size="small" onClick={() => setFiles(files.filter((_, j) => j !== i))} sx={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: '1px solid #e2e8f0', p: 0.25 }}><MdClose size={12} /></IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              {sendError && <Alert severity="error" onClose={() => setSendError('')} sx={{ mb: 1.25 }}>{sendError}</Alert>}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <IconButton component="label" disabled={sending}><FaPaperclip size={18} color="rgb(202 138 4)" /><input type="file" hidden multiple accept="image/*" onChange={pickFiles} /></IconButton>
                <TextField fullWidth multiline maxRows={5} size="small" placeholder="Write a reply… (Enter to send, Shift+Enter for a new line)" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={sending} />
                <IconButton onClick={send} disabled={sending || (!text.trim() && files.length === 0)} sx={{ bgcolor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { bgcolor: 'rgb(255 190 0)' }, '&.Mui-disabled': { opacity: 0.5 } }}>
                  {sending ? <CircularProgress size={18} /> : <IoSendSharp size={18} />}
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: '#64748b' }}>This ticket is resolved{query.resolvedAt ? ` on ${dt(query.resolvedAt)}` : ''}. Reopen it to reply.</Typography>
            </Box>
          )}
        </Box>

        {/* side panel */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #22c55e', borderRadius: '14px', p: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1.25 }}>Manage</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <TextField select size="small" label="Priority" value={query.priority || 'normal'} disabled={working} onChange={(e) => change({ priority: e.target.value })}>
                <MenuItem value="high">High</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="low">Low</MenuItem>
              </TextField>
              <TextField select size="small" label="Assigned to" value={query.assignedTo?._id || ''} disabled={working} onChange={(e) => change({ assignedTo: e.target.value || null })}>
                <MenuItem value="">Unassigned</MenuItem>
                {staff.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
          <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #0ea5e9', borderRadius: '14px', p: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 0.5 }}>Customer</Typography>
            <Row label="Name" value={customerName} />
            <Row label="Mobile" value={query.customerId?.mobile} />
            <Row label="Email" value={query.customerId?.email} />
            <Row label="Order" value={query.orderId?.orderNumber ? <Chip size="small" label={`#${query.orderId.orderNumber}`} sx={{ fontWeight: 700, height: 20 }} /> : null} />
            <Row label="Order status" value={query.orderId?.orderStatus} />
          </Box>
        </Box>
      </Box>

      <Dialog open={!!confirm} onClose={() => !working && setConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>{confirm === 'resolved' ? 'Resolve this ticket?' : 'Reopen this ticket?'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            {confirm === 'resolved' ? 'The customer is told it was resolved, and replies are closed until you reopen it.' : 'The ticket goes back to Open and you can reply again.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={working} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button disabled={working} onClick={async () => { await change({ status: confirm }); setConfirm(null); }} variant="contained" sx={confirm === 'resolved' ? { textTransform: 'none', fontWeight: 800, background: '#16a34a', '&:hover': { background: '#15803d' } } : yellowBtn}>
            {working ? 'Working…' : confirm === 'resolved' ? 'Resolve' : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chat;
